/**
 * ShareInviteSheet — iter127.
 *
 * Universal invite-share bottom sheet. Replaces the inline invite Dialog in
 * GroupDetailPage as the canonical share UI. Reused from Group header,
 * Group ⋯ menu, Home My-Groups row, and long-press context menu.
 *
 * Contents:
 *   1. Rich OG preview image (recipients' view)
 *   2. Primary CTA — "Share" (Web Share API → falls back to copy rich link)
 *   3. WhatsApp green button — opens https://wa.me/?text=… directly
 *   4. Copy plain link button
 *   5. QR code for face-to-face scans
 *   6. Expiry / max-uses note
 */
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Share2, Copy, MessageCircle, QrCode, Sparkles } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Card } from './ui/card';

const APP_BRAND = process.env.REACT_APP_BRAND_NAME || 'SplitEasy';

function buildInviteText(groupName, richUrl) {
  return `You're invited to join ${groupName || 'our group'} on ${APP_BRAND}. Track shared expenses, settle in 2 taps — no real money moves. ${richUrl}`;
}

export default function ShareInviteSheet({ open, onOpenChange, group, code, loading }) {
  // iter134 — prefer the branded origin the user is browsing on (e.g. split-easy.in)
  // over REACT_APP_BACKEND_URL (which is the raw emergent.host API origin). The
  // /api/i/{code} route works on both because Kubernetes ingress proxies /api/*
  // to the backend regardless of which hostname is in the URL.
  const richUrl = code
    ? `${window.location.origin}/api/i/${code}`
    : '';
  const plainUrl = code
    ? `${window.location.origin}/join/${code}`
    : '';
  const message = buildInviteText(group?.name, richUrl);

  const handleNativeShare = async () => {
    if (!richUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: group?.name || 'SplitEasy invite',
          text: message,
          url: richUrl,
        });
        return;
      } catch (e) { if (e?.name === 'AbortError') return; }
    }
    // fallback
    try {
      await navigator.clipboard.writeText(richUrl);
      toast.success('Rich-preview link copied — paste into WhatsApp');
    } catch { toast.error('Failed to copy'); }
  };

  const handleWhatsApp = () => {
    if (!richUrl) return;
    const text = encodeURIComponent(message);
    // wa.me is universal — opens in WhatsApp app on mobile, web on desktop.
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleCopyPlain = async () => {
    if (!plainUrl) return;
    try {
      await navigator.clipboard.writeText(plainUrl);
      toast.success('Invite link copied!');
    } catch { toast.error('Failed to copy'); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[92vh] overflow-y-auto" data-testid="share-invite-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Invite to {group?.name || 'group'}
          </SheetTitle>
          <SheetDescription>
            Anyone with this link can join. Expires in 7 days · max 50 uses.
          </SheetDescription>
        </SheetHeader>

        {loading || !code ? (
          <div className="py-10 flex flex-col items-center justify-center gap-3" data-testid="share-invite-loading">
            <Sparkles className="w-8 h-8 text-primary animate-pulse" />
            <p className="text-sm text-muted-foreground">Generating invite link…</p>
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {/* Invite code display */}
            <Card className="p-3 rounded-2xl border-2 bg-muted/30" data-testid="share-invite-code">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Invite code</p>
              <p className="font-display text-xl font-bold text-primary tracking-widest mt-0.5">{code}</p>
            </Card>

            {/* Rich preview thumbnail */}
            <div className="rounded-2xl overflow-hidden border-2 border-border shadow-sm" data-testid="share-invite-og-preview">
              <img
                src={`${window.location.origin}/api/invites/${code}/og.png`}
                alt="Invite preview"
                className="w-full h-auto block"
                loading="lazy"
              />
              <div className="px-3 py-1.5 bg-muted/30 text-xs text-muted-foreground text-center font-semibold uppercase tracking-wider">
                What recipients see in WhatsApp
              </div>
            </div>

            {/* Primary CTAs */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleWhatsApp}
                className="h-12 rounded-full font-bold bg-[#25D366] hover:bg-[#1ebd5b] text-white"
                data-testid="share-invite-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                WhatsApp
              </Button>
              <Button
                onClick={handleNativeShare}
                variant="outline"
                className="h-12 rounded-full font-bold border-2"
                data-testid="share-invite-native"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                More
              </Button>
            </div>

            <Button
              onClick={handleCopyPlain}
              variant="ghost"
              className="w-full h-10 rounded-full text-xs font-bold uppercase tracking-wider"
              data-testid="share-invite-copy"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              Copy plain link
            </Button>

            {/* QR for face-to-face */}
            <div className="pt-2 border-t border-border/60">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-center mb-2 inline-flex items-center gap-1 justify-center w-full">
                <QrCode className="w-3 h-3" /> Or scan in person
              </p>
              <div className="flex justify-center">
                <Card className="p-3 bg-white border-2 border-border rounded-2xl" data-testid="share-invite-qr">
                  <QRCodeSVG
                    value={plainUrl}
                    size={160}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0a0a0a"
                    includeMargin={false}
                  />
                </Card>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-1.5 break-all">{plainUrl}</p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
